/* @jsxImportSource @emotion/react */

import { useEffect, useState } from 'react'

import { Section } from '../Section'
import type { WebsiteNavbarProps } from './Navbar'
import { WebsiteNavbar } from './Navbar'

export const NAVBAR_HEIGHT = 64
const SCROLL_THRESHOLD = 8

export const NavbarSection = ({
  className,
  background = 'shade-100',
  fullWidth = false,
  maxWidth,
  topOffset = 0,
  // `transparent` = float as an overlay (static). `inverted` = currently show
  // the transparent/light look; the page (e.g. via an IntersectionObserver on
  // its hero) flips this, and the bar turns solid when it's false.
  transparent = false,
  inverted = true,
  minimalCta,
}: WebsiteNavbarProps & {
  fullWidth?: boolean
  maxWidth?: number
  topOffset?: number
  transparent?: boolean
}) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Only used to fade in the divider shadow on non-overlay pages.
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > SCROLL_THRESHOLD)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // The mobile menu always forces the solid look so the bar matches the panel.
  const isOverlay = transparent && inverted && !isMenuOpen
  const showDivider = transparent ? !isOverlay : isScrolled

  return (
    <div
      style={{
        height: transparent ? 0 : `${NAVBAR_HEIGHT + topOffset}px`,
        backgroundColor: transparent ? 'transparent' : `var(--${background})`,
      }}
    >
      <header
        className="position-fixed flexbox flexbox-align-center"
        style={{
          height: `${NAVBAR_HEIGHT}px`,
          backgroundColor: isOverlay ? 'transparent' : `var(--${background})`,
          boxShadow: showDivider
            ? '0 1px 0 0 rgba(0, 0, 0, 0.06)'
            : '0 1px 0 0 rgba(0, 0, 0, 0)',
          top: `${topOffset}px`,
        }}
        css={{
          zIndex: 1000,
          left: 0,
          right: 0,
          transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
        }}
      >
        <Section
          background={isOverlay ? undefined : background}
          className="width-full"
          containerVerticalPadding={0}
          maxWidth={maxWidth ?? (fullWidth ? 'none' : undefined)}
          containerLeftPadding={
            maxWidth != null ? 24 : fullWidth ? 20 : undefined
          }
          containerRightPadding={
            maxWidth != null ? 24 : fullWidth ? 20 : undefined
          }
        >
          <WebsiteNavbar
            className={className}
            background={background}
            centeredMenu={fullWidth}
            inverted={isOverlay}
            onMenuOpenChange={setIsMenuOpen}
            minimalCta={minimalCta}
          />
        </Section>
      </header>
    </div>
  )
}
